import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { legalDisclaimer } from '../siteContent';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticleDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const isAuth = !!token;

  useEffect(() => {
    if (!isAuth) { setLoading(false); return; }
    axios.get(`${API}/articles/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Not logged in — show gate
  if (!isAuth) {
    return (
      <div className="bg-[#FDFBF9] min-h-[70vh] flex items-center justify-center" data-testid="article-gate">
        <div className="max-w-md mx-auto text-center px-6 py-16">
          <div className="w-16 h-16 rounded-2xl bg-[#003366]/5 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#003366]" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">Members Only Article</h2>
          <p className="text-[#666] mt-3 leading-relaxed">This article is available to Equaltoo members. Join free to read this and all other resources in full.</p>
          <Link to="/membership" className="block mt-6">
            <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 w-full group" data-testid="article-gate-join-btn">
              Join Free to Access <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <Link to="/login" className="block mt-3 text-sm text-[#003366] hover:text-[#E74C3C] transition-colors">Already a member? Log in</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF9] text-[#666666]">Loading…</div>;
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#FDFBF9] gap-4" data-testid="article-not-found">
        <p className="text-[#666666]">Article not found.</p>
        <Link to="/knowledge-hub"><Button variant="outline" className="rounded-full">Back to Knowledge Hub</Button></Link>
      </div>
    );
  }

  return (
    <article className="bg-[#FDFBF9]" data-testid="article-detail-page">
      <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <Link to="/knowledge-hub" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#003366] hover:text-[#E74C3C] mb-8">
          <ArrowLeft className="w-4 h-4" /> Knowledge Hub
        </Link>
        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#E74C3C] mb-4">{article.category}</span>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-tight">{article.title}</h1>
        <p className="mt-4 text-sm text-[#666666]">By {article.author} · {new Date(article.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        {article.cover_image ? (
          <img src={article.cover_image} alt={article.title} className="rounded-3xl w-full h-72 object-cover mt-8" loading="lazy" decoding="async" />
        ) : null}
        {article.excerpt ? (
          <p className="mt-8 text-xl text-[#333] font-medium leading-relaxed">{article.excerpt}</p>
        ) : null}
        <div className="mt-6 text-lg text-[#444] leading-relaxed whitespace-pre-line">{article.body}</div>
        {article.link ? (
          <a href={article.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-8 text-[#E74C3C] font-medium">
            Read the full resource <ArrowRight className="w-4 h-4" />
          </a>
        ) : null}
        <div className="mt-14 rounded-3xl bg-[#003366] text-white p-8 text-center">
          <h3 className="font-heading text-2xl font-bold">Learn more with the community — free.</h3>
          <Link to="/membership">
            <Button className="mt-5 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 group">
              Join Free Community <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
        <p className="mt-10 text-xs text-[#666666] leading-relaxed">{legalDisclaimer}</p>
      </div>
    </article>
  );
};

export default ArticleDetail;
